<script setup lang="ts">
import { onMounted, ref, computed, watch } from "vue";
import { formatNumber } from "chartjs-graph";
import DashboardCardDO = App.Data.DashboardCardDO;
import ValueFormat = App.Enums.ValueFormat;
import SingleMetricResponseDTO = App.Data.Explorer.DataTable.SingleMetricResponseDTO;
import { ApiError, Result } from "@/api/base";
import { buildSingleMetricTitle } from "@/modules/explorer/components/SingleMetric/util";

const props = defineProps<{
	card: DashboardCardDO;
	fetchDataForSingleMetric: () => Promise<
		Result<{ singleMetric: SingleMetricResponseDTO }, ApiError>
	>;
	fetchTargetForSingleMetric: () => Promise<
		Result<{ singleMetric: SingleMetricResponseDTO }, ApiError>
	>;
	lastUpdated: number;
}>();

const emit = defineEmits(["update:isLoading", "update:derivedDocument"]);

const trackColor = "rgba(var(--v-theme-on-surface), 0.12)";

const rawValue = ref<number | null>(null);
const valueFormat = ref<ValueFormat>("default");

const targetMetricValue = ref<number | null>(null);
const targetFormat = ref<ValueFormat>("default");

const usesTargetDocument = computed(() => !!props.card.target_stored_document);

const rangeColor = computed(
	() => props.card.range_color ?? "rgb(var(--v-theme-success))"
);

const resolvedStyle = computed(() => props.card.progress_style ?? "bar");

const legendLabel = computed(() =>
	buildSingleMetricTitle(props.card.stored_document?.activeDocument)
);

const targetLegendLabel = computed(() => {
	if (usesTargetDocument.value) {
		return buildSingleMetricTitle(
			props.card.target_stored_document?.activeDocument,
			props.card.target_percentage
		);
	}

	return resolvedTarget.value !== null ? "Fixed target value" : "";
});

const isRange = computed(() => !!props.card.target_is_range);

// A bound is absolute for a fixed target, or a percentage of the target metric
// when a target document is used.
const resolveBound = (raw?: number | null): number | null => {
	if (raw === null || raw === undefined) return null;
	if (!usesTargetDocument.value) return raw;
	if (targetMetricValue.value === null) return null;

	return targetMetricValue.value * (raw / 100);
};

const resolvedTarget = computed(() =>
	usesTargetDocument.value
		? resolveBound(props.card.target_percentage)
		: props.card.target ?? null
);

const resolvedLower = computed(() =>
	isRange.value ? resolveBound(props.card.target_min) : null
);
const resolvedUpper = computed(() =>
	isRange.value ? resolveBound(props.card.target_max) : null
);

const scaleFormat = computed(() =>
	usesTargetDocument.value ? targetFormat.value : valueFormat.value
);

const scaleMax = computed(() => {
	if (isRange.value) {
		const upper = resolvedUpper.value ?? resolvedLower.value;
		if (!upper) return null;

		const candidate = Math.max(upper * 1.25, rawValue.value ?? 0);

		// Percentage values are stored as fractions (e.g. 0.113 = 11.3%), so
		// round up to the nearest 10 percentage points rather than nearest 10.
		if (scaleFormat.value === "percentage") {
			return Math.ceil((candidate * 100) / 10) / 10;
		}

		return Math.ceil(candidate / 10) * 10;
	}

	const target = resolvedTarget.value;
	if (target === null) return null;
	if (rawValue.value === null) return target;

	// The scale normally tops out at the target, but if the value overshoots
	// it, extend the scale to the value instead so the fill doesn't clip —
	// the original target position is then marked with a line (bar only, for
	// now).
	return Math.max(target, rawValue.value);
});

const percentage = computed(() => {
	const max = scaleMax.value;
	if (!max || rawValue.value === null) return 0;

	return Math.min(100, Math.max(0, (rawValue.value / max) * 100));
});

const boundToPercent = (bound: number | null): number | null => {
	const max = scaleMax.value;
	if (!max || bound === null) return null;

	return Math.min(100, Math.max(0, (bound / max) * 100));
};
const lowerMarkerPct = computed(() => boundToPercent(resolvedLower.value));
const upperMarkerPct = computed(() => boundToPercent(resolvedUpper.value));

// In single-target mode, the scale can be stretched past the target when the
// value overshoots it (see scaleMax) — mark where the target originally was.
const showsTargetOvershootMarker = computed(() => {
	if (isRange.value) return false;

	const target = resolvedTarget.value;
	if (target === null || rawValue.value === null) return false;

	return rawValue.value > target;
});
const targetMarkerPct = computed(() => boundToPercent(resolvedTarget.value));

const isTargetReached = computed(() => {
	if (rawValue.value === null) return false;

	if (isRange.value) {
		const lower = resolvedLower.value;
		const upper = resolvedUpper.value;
		if (lower === null && upper === null) return false;

		return (
			(lower === null || rawValue.value >= lower) &&
			(upper === null || rawValue.value <= upper)
		);
	}

	const target = resolvedTarget.value;

	return !!target && target > 0 && rawValue.value >= target;
});

const displayValue = computed(() => {
	if (rawValue.value === null) return "–";

	return formatNumber(rawValue.value, valueFormat.value);
});

const displayStart = computed(() => {
	return formatNumber(
		0,
		usesTargetDocument.value ? targetFormat.value : valueFormat.value
	);
});

const displayBound = (bound: number | null): string =>
	bound === null
		? "–"
		: formatNumber(
				bound,
				usesTargetDocument.value ? targetFormat.value : valueFormat.value
		  );

const displayTarget = computed(() => displayBound(resolvedTarget.value));

const displayTargetLabel = computed(() => {
	if (!isRange.value) return displayTarget.value;

	const lo = resolvedLower.value;
	const hi = resolvedUpper.value;
	if (lo !== null && hi !== null) {
		return `${displayBound(lo)}–${displayBound(hi)}`;
	}
	if (lo !== null) return `≥ ${displayBound(lo)}`;
	if (hi !== null) return `≤ ${displayBound(hi)}`;

	return "–";
});

const displayRangeStart = computed(() => displayStart.value);
const displayRangeEnd = computed(() => displayBound(scaleMax.value));

const valueFontSize = (base: number) => {
	const length = displayValue.value.length;
	if (length <= 4) return base;
	if (length <= 7) return base * 0.78;

	return base * 0.58;
};
const circleValueFont = computed(() => valueFontSize(34));

const circleRadius = 58;
const circleCircumference = 2 * Math.PI * circleRadius;
const circleDashOffset = computed(
	() => circleCircumference * (1 - percentage.value / 100)
);

const gaugeRadius = 90;
const gaugePathLength = Math.PI * gaugeRadius;
const gaugeDashOffset = computed(
	() => gaugePathLength * (1 - percentage.value / 100)
);
const gaugePath = `M 10 110 A ${gaugeRadius} ${gaugeRadius} 0 0 1 190 110`;

// Range band: an area indicating the target range, drawn over the track.
// When only one bound is set, the band fills to that end of the track.
const bandStartPct = computed(() => lowerMarkerPct.value ?? 0);
const bandEndPct = computed(() => upperMarkerPct.value ?? 100);
const bandLengthPct = computed(() =>
	Math.max(0, bandEndPct.value - bandStartPct.value)
);

const arcBand = (total: number) => {
	const length = (bandLengthPct.value / 100) * total;

	return {
		dashArray: `${length} ${total - length}`,
		dashOffset: -(bandStartPct.value / 100) * total,
	};
};

const circleBand = computed(() => arcBand(circleCircumference));
const gaugeBand = computed(() => arcBand(gaugePathLength));

// Target overshoot marker (see scaleMax): a short radial line crossing the
// ring at the target's original position, for the circle and gauge styles.
const CIRCLE_CENTER = { x: 70, y: 70 };
const GAUGE_CENTER = { x: 100, y: 110 };
const MARKER_RADIAL_REACH = 10;

const radialMarkerLine = (
	center: { x: number; y: number },
	radius: number,
	angleDeg: number
) => {
	const angleRad = (angleDeg * Math.PI) / 180;
	const inner = radius - MARKER_RADIAL_REACH;
	const outer = radius + MARKER_RADIAL_REACH;

	return {
		x1: center.x + inner * Math.cos(angleRad),
		y1: center.y + inner * Math.sin(angleRad),
		x2: center.x + outer * Math.cos(angleRad),
		y2: center.y + outer * Math.sin(angleRad),
	};
};

const circleTargetMarkerLine = computed(() => {
	if (!showsTargetOvershootMarker.value || targetMarkerPct.value === null) {
		return null;
	}

	// The circle's arcs sweep a full 360° from the top (12 o'clock, via their
	// -90° transform), so 0% sits at angle -90°.
	const angleDeg = (targetMarkerPct.value / 100) * 360 - 90;

	return radialMarkerLine(CIRCLE_CENTER, circleRadius, angleDeg);
});

const gaugeTargetMarkerLine = computed(() => {
	if (!showsTargetOvershootMarker.value || targetMarkerPct.value === null) {
		return null;
	}

	// The gauge sweeps 180° (left, 0%) to 360°/0° (right, 100%) through the top.
	const angleDeg = 180 + (targetMarkerPct.value / 100) * 180;

	return radialMarkerLine(GAUGE_CENTER, gaugeRadius, angleDeg);
});

const extractMetric = (
	response: Result<{ singleMetric: SingleMetricResponseDTO }, ApiError>
): { value: number | null; format: ValueFormat } => {
	if (response.isOk()) {
		const metric = response.value?.singleMetric;
		return {
			value: typeof metric?.data === "number" ? metric.data : null,
			format: metric?.format ?? "default",
		};
	}

	return { value: null, format: "default" };
};

const refresh = async () => {
	emit("update:isLoading", true);

	const tasks: Promise<void>[] = [
		props.fetchDataForSingleMetric().then((response) => {
			const { value, format } = extractMetric(response);
			rawValue.value = value;
			valueFormat.value = format;
			emit("update:derivedDocument", response.value?.derivedDocument);
		}),
	];

	if (usesTargetDocument.value) {
		tasks.push(
			props.fetchTargetForSingleMetric().then((response) => {
				const { value, format } = extractMetric(response);
				targetMetricValue.value = value;
				targetFormat.value = format;
			})
		);
	}

	await Promise.all(tasks);

	emit("update:isLoading", false);
};

onMounted(refresh);
watch(
	() => props.lastUpdated,
	() => refresh()
);
</script>

<template>
	<div
		class="progress-card d-flex flex-column h-100 w-100"
		:class="{ 'is-reached': isTargetReached }"
		:style="{ '--pc-color': card.value_color }"
	>
		<!-- Bar -->
		<div
			v-if="resolvedStyle === 'bar'"
			class="pc-chart d-flex flex-column justify-center"
		>
			<div class="d-flex align-end ga-1 pc-bar-head">
				<span class="pc-value-bar">{{ displayValue }}</span>
				<span class="pc-caption"> {{ displayTargetLabel }} </span>
			</div>
			<div class="pc-bar-track-wrap">
				<div class="pc-bar-track">
					<div
						class="pc-bar-fill"
						:style="{
							width: percentage + '%',
							backgroundColor: card.value_color,
						}"
					/>
				</div>
				<div
					v-if="isRange"
					class="pc-range-band"
					:style="{
						left: bandStartPct + '%',
						width: bandLengthPct + '%',
						backgroundColor: rangeColor,
					}"
				/>
				<div
					v-if="showsTargetOvershootMarker"
					class="pc-target-marker"
					:style="{ left: targetMarkerPct + '%' }"
				/>
			</div>
			<div class="d-flex justify-space-between pc-range">
				<span>{{ displayRangeStart }}</span>
				<span>{{ displayRangeEnd }}</span>
			</div>
		</div>

		<!-- Circle -->
		<div
			v-else-if="resolvedStyle === 'circle'"
			class="pc-chart d-flex flex-column align-center justify-center"
		>
			<div class="pc-svg-wrap">
				<svg
					viewBox="0 0 140 140"
					preserveAspectRatio="xMidYMid meet"
					class="pc-circle-svg"
				>
					<circle
						cx="70"
						cy="70"
						:r="circleRadius"
						fill="none"
						:stroke="trackColor"
						stroke-width="14"
					/>
					<circle
						v-if="isRange"
						cx="70"
						cy="70"
						:r="circleRadius"
						fill="none"
						:stroke="rangeColor"
						stroke-opacity="0.3"
						stroke-width="20"
						stroke-linecap="butt"
						:stroke-dasharray="circleBand.dashArray"
						:stroke-dashoffset="circleBand.dashOffset"
						transform="rotate(-90 70 70)"
					/>
					<circle
						cx="70"
						cy="70"
						:r="circleRadius"
						fill="none"
						:stroke="card.value_color"
						stroke-width="14"
						stroke-linecap="butt"
						:stroke-dasharray="circleCircumference"
						:stroke-dashoffset="circleDashOffset"
						transform="rotate(-90 70 70)"
						class="pc-arc"
					/>
					<line
						v-if="circleTargetMarkerLine"
						:x1="circleTargetMarkerLine.x1"
						:y1="circleTargetMarkerLine.y1"
						:x2="circleTargetMarkerLine.x2"
						:y2="circleTargetMarkerLine.y2"
						class="pc-target-marker-line"
					/>
					<text
						x="70"
						y="64"
						text-anchor="middle"
						dominant-baseline="central"
						:font-size="circleValueFont"
						class="pc-value-circle number-mono"
					>
						{{ displayValue }}
					</text>
					<text
						x="70"
						y="80"
						text-anchor="middle"
						dominant-baseline="central"
						:font-size="circleValueFont"
						class="pc-caption-circle number-mono"
					>
						{{ displayTargetLabel }}
					</text>
				</svg>
			</div>
		</div>

		<!-- Gauge -->
		<div
			v-else-if="resolvedStyle === 'gauge'"
			class="pc-chart d-flex flex-column align-center justify-center"
		>
			<div class="pc-svg-wrap">
				<svg
					viewBox="-15 0 230 140"
					preserveAspectRatio="xMidYMid meet"
					class="pc-gauge-svg"
				>
					<path
						:d="gaugePath"
						fill="none"
						:stroke="trackColor"
						stroke-width="14"
					/>
					<path
						v-if="isRange"
						:d="gaugePath"
						fill="none"
						:stroke="rangeColor"
						stroke-opacity="0.3"
						stroke-width="20"
						:stroke-dasharray="gaugeBand.dashArray"
						:stroke-dashoffset="gaugeBand.dashOffset"
					/>
					<path
						:d="gaugePath"
						fill="none"
						:stroke="card.value_color"
						stroke-width="14"
						:stroke-dasharray="gaugePathLength"
						:stroke-dashoffset="gaugeDashOffset"
						class="pc-arc"
					/>
					<line
						v-if="gaugeTargetMarkerLine"
						:x1="gaugeTargetMarkerLine.x1"
						:y1="gaugeTargetMarkerLine.y1"
						:x2="gaugeTargetMarkerLine.x2"
						:y2="gaugeTargetMarkerLine.y2"
						class="pc-target-marker-line"
					/>
					<text
						x="100"
						y="83"
						text-anchor="middle"
						dominant-baseline="central"
						:font-size="circleValueFont"
						class="pc-value-circle number-mono"
					>
						{{ displayValue }}
					</text>
					<text
						x="100"
						y="99"
						text-anchor="middle"
						dominant-baseline="central"
						:font-size="circleValueFont"
						class="pc-caption-circle number-mono"
					>
						{{ displayTargetLabel }}
					</text>
					<text x="10" y="123" text-anchor="middle" class="pc-svg-range">
						{{ displayRangeStart }}
					</text>
					<text x="190" y="123" text-anchor="middle" class="pc-svg-range">
						{{ displayRangeEnd }}
					</text>
				</svg>
			</div>
		</div>

		<v-divider class="pc-divider" />
		<div class="d-flex align-center pc-legend-wrap">
			<div class="d-flex flex-column flex-grow-1 pc-legend">
				<div class="d-flex align-center pc-legend__row">
					<span
						class="pc-legend-dot"
						:style="{ backgroundColor: card.value_color }"
					/>
					<span class="pc-legend__label">{{ legendLabel }}</span>
				</div>
				<div
					v-if="targetLegendLabel"
					class="d-flex align-center pc-legend__row"
				>
					<span
						class="pc-legend-dot"
						:style="{ backgroundColor: trackColor }"
					/>
					<span class="pc-legend__label">{{ targetLegendLabel }}</span>
				</div>
				<div v-if="isRange" class="d-flex align-center pc-legend__row">
					<span
						class="pc-legend-dot"
						:style="{ backgroundColor: rangeColor, opacity: 0.3 }"
					/>
					<span class="pc-legend__label">Target Range</span>
				</div>
			</div>
		</div>
	</div>
</template>

<style scoped lang="scss">
.progress-card {
	container-type: size;
	position: relative;
	// Bar track thickness tuned to match the circle/gauge ring thickness
	// (14 of a 140 viewBox height, ~10% of the chart area's height).
	--pc-thickness: clamp(8px, 10cqh, 20px);

	.pc-chart {
		flex: 1;
		min-height: 0;
	}

	.pc-value-bar {
		font-size: clamp(1.1rem, 24cqmin, 2.15rem);
		font-weight: 700;
		line-height: 1;
		font-variant-numeric: tabular-nums;
	}

	.pc-value-circle {
		font-size: 1.1rem;
		font-weight: 700;
		line-height: 1;
		font-variant-numeric: tabular-nums;
	}

	.pc-bar-head {
		margin-bottom: clamp(0.5rem, 4cqmin, 0.9rem);
	}

	.pc-caption {
		font-size: clamp(0.8rem, 17cqmin, 1.5rem);
		font-weight: 600;
		line-height: 1;
		opacity: 0.5;
		white-space: nowrap;
		margin-left: clamp(0.4rem, 3cqmin, 0.75rem);
	}

	.pc-caption-circle {
		font-size: 0.6rem;
		font-weight: 600;
		line-height: 1;
		opacity: 0.5;
		white-space: nowrap;
	}

	.pc-bar-track-wrap {
		position: relative;
	}

	.pc-bar-track {
		position: relative;
		width: 100%;
		height: var(--pc-thickness);
		background: rgba(var(--v-theme-on-surface), 0.12);
		overflow: hidden;
	}

	.pc-bar-fill {
		height: 100%;
		transition: width 0.3s ease;
	}

	// Opaque area indicating the target range, wider than the track so it
	// stands out from both the value fill and the track itself.
	.pc-range-band {
		position: absolute;
		top: -3px;
		bottom: -3px;
		border-radius: 2px;
		opacity: 0.3;
	}

	// Marks where the target originally was when the value has overshot it
	// and the scale stretched to fit the value instead.
	.pc-target-marker {
		position: absolute;
		top: -5px;
		bottom: -5px;
		width: 3px;
		background: #fff;
		opacity: 1;
		transform: translateX(-50%);
	}

	.pc-target-marker-line {
		stroke: #fff;
		stroke-width: 3;
		stroke-opacity: 1;
	}

	.pc-range {
		font-size: clamp(0.6rem, 10cqmin, 1rem);
		opacity: 0.5;
		font-variant-numeric: tabular-nums;
		margin-top: clamp(0.15rem, 1cqmin, 0.25rem);
	}

	.pc-svg-wrap {
		flex: 1;
		min-height: 0;
		width: 100%;
		display: flex;
		align-items: center;
		justify-content: center;

		svg {
			display: block;
		}

		// Square: fits the smaller dimension and scales with the card.
		.pc-circle-svg {
			width: 100%;
			height: 100%;
		}

		.pc-gauge-svg {
			height: 100%;
			width: auto;
			max-width: 100%;
			aspect-ratio: 230 / 140;
		}
	}

	.pc-svg-value {
		font-weight: 700;
		fill: rgb(var(--v-theme-on-surface));
		font-variant-numeric: tabular-nums;
	}

	.pc-svg-range {
		font-size: 0.5rem;
		fill: rgb(var(--v-theme-on-surface));
		opacity: 0.5;
		font-variant-numeric: tabular-nums;
	}

	.pc-divider {
		margin: clamp(0.4rem, 3cqmin, 1rem) 0 clamp(0.25rem, 2cqmin, 0.5rem);
	}

	.pc-legend {
		font-size: clamp(0.6rem, 5cqmin, 0.75rem);
	}

	.pc-legend__row {
		gap: 0.375rem;
		min-width: 0;
	}

	.pc-legend__label {
		opacity: 0.6;
		font-size: 0.875rem;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.pc-legend-dot {
		width: 12px;
		height: 12px;
		border-radius: 50%;
		flex-shrink: 0;
	}

	// --- Target reached celebration ---
	.pc-legend-wrap {
		gap: 0.5rem;
		min-height: 30px;
	}

	&.is-reached {
		//.pc-bar-fill,
		//.pc-arc {
		//	// Static soft glow; animated below when motion is allowed.
		//	filter: drop-shadow(0 0 3px var(--pc-color));
		//}
	}
}

@keyframes pc-glow {
	0%,
	100% {
		filter: drop-shadow(0 0 1px var(--pc-color));
	}
	50% {
		filter: drop-shadow(0 0 6px var(--pc-color));
	}
}
</style>
